import React, { forwardRef, isValidElement, useMemo, useState } from 'react';
import { bool, func, node, oneOf, oneOfType, shape, string } from 'prop-types';

import Typography from '@/components/Typography';

import { styTextWrapper, styTextInput, styTextAppend, styTextPrepend } from './style';

const InputText = forwardRef((props, ref) => {
  const {
    id,
    name,
    append,
    block,
    bold,
    className,
    defaultValue,
    disabled,
    error,
    onAppendClick,
    onBlur,
    onChange,
    onEnter,
    onKeyDown,
    onPrependClick,
    placeholder,
    prepend,
    size,
    style,
    textHint,
    type,
    value: propsValue,
    borderColor,
    ...inputProps
  } = props;

  // Store ref to element using state to trigger re-render on mount
  const [appendEl, setAppendEl] = useState(null);
  const [prependEl, setPrependEl] = useState(null);

  // Store value on state when component is uncontrolled
  const isControlled = useMemo(() => propsValue !== undefined, [propsValue]);
  const [stateValue, setStateValue] = useState(defaultValue);

  const handleChange = (_event) => {
    if (disabled) return;

    const event = { ...new Event('change') };
    event.target = { value: _event.target.value };
    onChange(event);

    if (!isControlled) setStateValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onEnter();
    }
    onKeyDown(event);
  };

  const styProps = {
    size,
    block,
    disabled,
    bold,
    error,
    append,
    appendClick: !!onAppendClick,
    appendPadding: appendEl?.offsetWidth || 36,
    prepend,
    prependClick: !!onPrependClick,
    prependPadding: prependEl?.offsetWidth || 36,
    width: inputProps.width,
    borderColor,
  };

  const renderAppendPrepend = (elem, spanProps) =>
    elem && (
      <span {...spanProps}>
        {isValidElement(elem) ? (
          elem
        ) : (
          <Typography type="body" bold={bold} style={{ margin: 0 }}>
            {elem}
          </Typography>
        )}
      </span>
    );

  const renderMessage = () => {
    switch (true) {
      case !!error:
        // If error exists, show error as highest priority
        return (
          <div>
            <Typography type="alert" alertType="error" style={{ marginTop: 4 }}>
              {error}
            </Typography>
          </div>
        );
      case !!textHint:
        // If no higher priorities present, display textHint if available
        return (
          <div>
            <Typography type="label" style={{ marginTop: 4 }}>
              {textHint}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <label htmlFor={id} className={styTextWrapper(styProps)}>
      {renderAppendPrepend(prepend, {
        className: styTextPrepend(styProps),
        ref: (v) => setPrependEl(v),
        onClick: onPrependClick,
      })}
      <input
        ref={ref}
        id={id}
        data-testid={id}
        name={name}
        type={type}
        style={style}
        className={`${styTextInput(styProps)} ${className}`}
        // Component is always controlled
        // If value is provided, use the provided value. Otherwise, use value stored on state
        value={isControlled ? propsValue : stateValue}
        // Do not pass defaultValue
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={disabled}
        {...inputProps}
      />
      {renderAppendPrepend(append, {
        className: styTextAppend(styProps),
        ref: (v) => setAppendEl(v),
        onClick: onAppendClick,
      })}
      {renderMessage()}
    </label>
  );
});

InputText.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  append: oneOfType([string, node]),
  block: bool,
  bold: bool,
  className: string,
  defaultValue: string,
  disabled: bool,
  error: string,
  onAppendClick: func,
  onBlur: func,
  onChange: func,
  onEnter: func,
  onKeyDown: func,
  onPrependClick: func,
  placeholder: string,
  prepend: oneOfType([string, node]),
  size: oneOf(['sm', 'md']),
  style: shape({}),
  textHint: string,
  type: oneOf(['email', 'number', 'password', 'text']),
  value: string,
  borderColor: string,
};

InputText.defaultProps = {
  append: '',
  block: false,
  bold: false,
  className: '',
  defaultValue: '',
  disabled: false,
  error: '',
  onAppendClick: null,
  onBlur: () => {},
  onChange: () => {},
  onEnter: () => {},
  onKeyDown: () => {},
  onPrependClick: null,
  placeholder: '',
  prepend: '',
  size: 'md',
  style: {},
  textHint: '',
  type: 'text',
  value: undefined,
  borderColor: '',
};

export default InputText;
